import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTenantBrandingStore } from '@/stores/tenant-branding'
import { STAFF_PERMISSIONS } from '@/shared/permissions'
import { normalizeStaffPermissionList } from '@/shared/staff-permission-list'
import { syncDocumentTitle } from '@/utils/document-title'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresAuth: false, titleKey: 'auth.signInTitle' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      meta: { requiresAuth: false, titleKey: 'auth.forgotPassword.title' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
      meta: { requiresAuth: false, titleKey: 'auth.resetPassword.title' },
    },
    {
      path: '/access-denied',
      name: 'access-denied',
      component: () => import('@/views/AccessDeniedView.vue'),
      meta: { requiresAuth: false, titleKey: 'accessDenied.title' },
    },
    {
      path: '/owner-impersonation',
      name: 'owner-impersonation',
      component: () => import('@/views/auth/OwnerImpersonationView.vue'),
      meta: { requiresAuth: false, titleKey: 'ownerImpersonation.title' },
    },
    {
      path: '/',
      component: () => import('@/components/layout/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: '/dashboard' },
        {
          path: 'setup',
          name: 'setup-gate',
          component: () => import('@/views/SetupGateView.vue'),
          meta: { titleKey: 'dashboard.readiness.title', ownerOnlyVendorScope: true },
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { titleKey: 'dashboard.title' },
        },
        {
          path: 'analytics',
          name: 'analytics',
          component: () => import('@/views/analytics/AnalyticsView.vue'),
          meta: {
            titleKey: 'analytics.title',
            permission: STAFF_PERMISSIONS.analyticsRead,
            requiresAnalytics: true,
            breadcrumbs: [{ labelKey: 'analytics.title' }],
          },
        },
        {
          path: 'pos',
          name: 'pos',
          component: () => import('@/views/pos/PosSellView.vue'),
          meta: {
            titleKey: 'pos.title',
            permissions: [STAFF_PERMISSIONS.ordersWrite, STAFF_PERMISSIONS.eventsRead],
            breadcrumbs: [{ labelKey: 'pos.title' }],
          },
        },
        {
          path: 'orders',
          name: 'orders',
          component: () => import('@/views/orders/OrdersListView.vue'),
          meta: {
            titleKey: 'orders.title',
            permission: STAFF_PERMISSIONS.ordersRead,
            breadcrumbs: [{ labelKey: 'orders.title' }],
          },
        },
        {
          path: 'orders/:id',
          name: 'order-detail',
          component: () => import('@/views/orders/OrderDetailView.vue'),
          meta: {
            titleKey: 'orders.detailTitle',
            permission: STAFF_PERMISSIONS.ordersRead,
            breadcrumbs: [
              { labelKey: 'orders.title', path: '/orders' },
              { labelKey: 'orders.detailTitle' },
            ],
          },
        },
        {
          path: 'finance/vouchers',
          name: 'vouchers',
          component: () => import('@/views/finance/VouchersListView.vue'),
          meta: {
            titleKey: 'vouchers.title',
            permission: STAFF_PERMISSIONS.financeRead,
            breadcrumbs: [{ labelKey: 'vouchers.title' }],
          },
        },
        {
          path: 'finance/vouchers/:id',
          name: 'voucher-detail',
          component: () => import('@/views/finance/VoucherDetailView.vue'),
          meta: {
            titleKey: 'vouchers.detailTitle',
            permission: STAFF_PERMISSIONS.financeRead,
            breadcrumbs: [
              { labelKey: 'vouchers.title', path: '/finance/vouchers' },
              { labelKey: 'vouchers.detailTitle' },
            ],
          },
        },
        {
          path: 'finance/gift-card-products',
          name: 'gift-card-products',
          component: () => import('@/views/finance/GiftCardProductsListView.vue'),
          meta: {
            titleKey: 'giftCardProducts.title',
            permission: STAFF_PERMISSIONS.financeRead,
            requiresGiftCards: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'giftCardProducts.title' }],
          },
        },
        {
          path: 'finance/gift-card-products/:id',
          name: 'gift-card-product-detail',
          component: () => import('@/views/finance/GiftCardProductDetailView.vue'),
          meta: {
            titleKey: 'giftCardProducts.detailTitle',
            permission: STAFF_PERMISSIONS.financeRead,
            requiresGiftCards: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'giftCardProducts.title', path: '/finance/gift-card-products' },
              { labelKey: 'giftCardProducts.detailTitle' },
            ],
          },
        },
        {
          path: 'reports/dashboard',
          redirect: '/dashboard',
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/views/users/StaffUsersView.vue'),
          meta: {
            titleKey: 'users.title',
            permissionsAny: [STAFF_PERMISSIONS.staffUsersRead, STAFF_PERMISSIONS.customersRead],
            breadcrumbs: [{ labelKey: 'users.title' }],
          },
        },
        {
          path: 'users/customers/:id',
          name: 'customer-detail',
          component: () => import('@/views/users/CustomerDetailView.vue'),
          meta: {
            titleKey: 'users.customerDetailTitle',
            permission: STAFF_PERMISSIONS.customersRead,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'users.title', path: '/users' },
              { labelKey: 'users.customerDetailTitle' },
            ],
          },
        },
        {
          path: 'users/:id',
          name: 'staff-user-detail',
          component: () => import('@/views/users/StaffUserDetailView.vue'),
          meta: {
            titleKey: 'users.detailTitle',
            permission: STAFF_PERMISSIONS.staffUsersRead,
            breadcrumbs: [
              { labelKey: 'users.title', path: '/users' },
              { labelKey: 'users.detailTitle' },
            ],
          },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/views/profile/StaffProfileView.vue'),
          meta: { titleKey: 'profile.title', breadcrumbs: [{ labelKey: 'profile.title' }] },
        },
        {
          path: 'vendors',
          redirect: { name: 'vendors' },
        },
        {
          path: 'vendors/management',
          name: 'vendors',
          component: () => import('@/views/vendors/VendorsView.vue'),
          meta: {
            titleKey: 'vendorsPage.title',
            permission: STAFF_PERMISSIONS.settingsWrite,
            requiresVendorSystem: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'vendorsPage.title', path: '/vendors/management' },
              { labelKey: 'nav.vendorManagement' },
            ],
          },
        },
        {
          path: 'vendors/reporting',
          name: 'vendor-reporting',
          component: () => import('@/views/vendors/VendorReportingView.vue'),
          meta: {
            titleKey: 'nav.vendorReporting',
            permission: STAFF_PERMISSIONS.settingsWrite,
            requiresVendorSystem: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'vendorsPage.title', path: '/vendors/management' },
              { labelKey: 'nav.vendorReporting' },
            ],
          },
        },
        {
          path: 'vendors/:id',
          name: 'vendor-detail',
          component: () => import('@/views/vendors/VendorDetailView.vue'),
          meta: {
            titleKey: 'vendorsPage.detail.fallbackTitle',
            permission: STAFF_PERMISSIONS.settingsWrite,
            requiresVendorSystem: true,
            ownerOnlyVendorScope: true,
            breadcrumbs: [
              { labelKey: 'vendorsPage.title', path: '/vendors/management' },
              { labelKey: 'vendorsPage.detail.fallbackTitle' },
            ],
          },
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/settings/SettingsView.vue'),
          meta: {
            titleKey: 'settings.title',
            permissionsAny: [STAFF_PERMISSIONS.settingsWrite, STAFF_PERMISSIONS.financeRead],
          },
          beforeEnter: (to) => {
            const legacyWebshopTabs: Record<string, string> = {
              general: 'maintenance',
              payments: 'checkout',
              branding: 'appearance',
            }
            const raw = to.query.tab
            const tab = Array.isArray(raw) ? raw[0] : raw
            if (typeof tab !== 'string') return true
            const nextTab = legacyWebshopTabs[tab]
            if (!nextTab) return true
            return {
              name: 'webshop',
              query: { tab: nextTab },
              hash: to.hash,
              replace: true,
            }
          },
        },
        {
          path: 'webshop',
          name: 'webshop',
          component: () => import('@/views/webshop/WebshopSettingsView.vue'),
          meta: {
            titleKey: 'webshop.pageTitle',
            permission: STAFF_PERMISSIONS.settingsWrite,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'webshop.pageTitle' }],
          },
          beforeEnter: (to) => {
            const legacyLegalTabs = new Set([
              'imprint',
              'privacy',
              'terms',
              'withdrawal',
              'cookies',
              'shipping',
              'accessibility',
            ])
            const raw = to.query.tab
            const tab = Array.isArray(raw) ? raw[0] : raw
            if (typeof tab === 'string' && legacyLegalTabs.has(tab)) {
              return {
                name: 'webshop',
                query: { ...to.query, tab: 'legal', legalTab: tab },
                hash: to.hash,
                replace: true,
              }
            }
            return true
          },
        },
        {
          path: 'automations',
          name: 'automations',
          component: () => import('@/views/automations/AutomationsView.vue'),
          meta: {
            titleKey: 'automations.title',
            permission: STAFF_PERMISSIONS.automationsManage,
            ownerOnlyVendorScope: true,
            breadcrumbs: [{ labelKey: 'automations.title' }],
          },
        },
        {
          path: 'support/cases',
          name: 'support-cases',
          component: () => import('@/views/support/SupportCasesListView.vue'),
          meta: {
            titleKey: 'support.casesTitle',
            permission: STAFF_PERMISSIONS.supportRead,
            breadcrumbs: [{ labelKey: 'support.casesTitle' }],
          },
        },
        {
          path: 'support/cases/:id',
          name: 'support-case-detail',
          component: () => import('@/views/support/SupportCaseDetailView.vue'),
          meta: {
            titleKey: 'support.caseDetailTitle',
            permission: STAFF_PERMISSIONS.supportRead,
            breadcrumbs: [
              { labelKey: 'support.casesTitle', path: '/support/cases' },
              { labelKey: 'support.caseDetailTitle' },
            ],
          },
        },
        {
          path: 'events',
          name: 'events',
          component: () => import('@/views/events/EventsListView.vue'),
          meta: {
            titleKey: 'events.title',
            permission: STAFF_PERMISSIONS.eventsRead,
            breadcrumbs: [{ labelKey: 'events.title' }],
          },
        },
        {
          path: 'events/new',
          name: 'event-new',
          component: () => import('@/views/events/EventFormView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsWrite,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.new' },
            ],
          },
        },
        {
          path: 'events/:id/edit',
          name: 'event-edit',
          component: () => import('@/views/events/EventFormView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsWrite,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.edit' },
            ],
          },
        },
        {
          path: 'events/:eventId/dates/new',
          name: 'event-date-new',
          redirect: (to) => ({
            name: 'event-detail',
            params: { id: String(to.params.eventId) },
            query: { addDate: '1' },
          }),
        },
        {
          path: 'events/:eventId/dates/:dateId',
          name: 'event-date-detail',
          component: () => import('@/views/events/EventDateDetailView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsRead,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.dateDetail' },
            ],
          },
        },
        {
          path: 'events/:id',
          name: 'event-detail',
          component: () => import('@/views/events/EventDetailView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsRead,
            breadcrumbs: [
              { labelKey: 'nav.events', path: '/events' },
              { labelKey: 'events.detailPageTitle' },
            ],
          },
        },
        {
          path: 'news',
          name: 'news',
          component: () => import('@/views/news/NewsListView.vue'),
          meta: {
            titleKey: 'nav.news',
            permission: STAFF_PERMISSIONS.newsRead,
            breadcrumbs: [{ labelKey: 'nav.news' }],
          },
        },
        {
          path: 'news/:id',
          name: 'news-detail',
          component: () => import('@/views/news/NewsDetailView.vue'),
          meta: {
            titleKey: 'news.detailTitle',
            permission: STAFF_PERMISSIONS.newsRead,
            breadcrumbs: [
              { labelKey: 'nav.news', path: '/news' },
              { labelKey: 'news.detailTitle' },
            ],
          },
        },
        {
          path: 'catalog/media',
          name: 'catalog-media',
          component: () => import('@/views/catalog/MediaLibraryView.vue'),
          meta: {
            titleKey: 'media.title',
            permission: STAFF_PERMISSIONS.mediaRead,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'media.title' },
            ],
          },
        },
        {
          path: 'catalog/locations',
          name: 'catalog-locations',
          component: () => import('@/views/catalog/CatalogLocationsView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'catalog.locationsTitle' },
            ],
          },
        },
        {
          path: 'catalog/genres',
          name: 'catalog-genres',
          component: () => import('@/views/catalog/CatalogGenresView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'catalog.genresTitle' },
            ],
          },
        },
        {
          path: 'catalog/artists',
          name: 'catalog-artists',
          component: () => import('@/views/catalog/CatalogArtistsView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'catalog.artistsTitle' },
            ],
          },
        },
        {
          path: 'catalog/locations/:id',
          name: 'catalog-location-detail',
          component: () => import('@/views/catalog/CatalogLocationDetailView.vue'),
          meta: {
            titleKey: 'catalog.locationDetailTitle',
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'catalog.locationsTitle', path: '/catalog/locations' },
              { labelKey: 'catalog.locationDetailTitle' },
            ],
          },
        },
        {
          path: 'catalog/genres/:id',
          name: 'catalog-genre-detail',
          component: () => import('@/views/catalog/CatalogGenreDetailView.vue'),
          meta: {
            titleKey: 'catalog.genreDetailTitle',
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'catalog.genresTitle', path: '/catalog/genres' },
              { labelKey: 'catalog.genreDetailTitle' },
            ],
          },
        },
        {
          path: 'catalog/artists/:id',
          name: 'catalog-artist-detail',
          component: () => import('@/views/catalog/CatalogArtistDetailView.vue'),
          meta: {
            titleKey: 'catalog.artistDetailTitle',
            permission: STAFF_PERMISSIONS.catalogRead,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'catalog.artistsTitle', path: '/catalog/artists' },
              { labelKey: 'catalog.artistDetailTitle' },
            ],
          },
        },
        {
          path: 'catalog/price-categories',
          name: 'catalog-price-categories',
          component: () => import('@/views/catalog/CatalogPriceCategoriesView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.eventsWrite,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'catalog.priceCategoriesTitle' },
            ],
          },
        },
        {
          path: 'catalog/seating',
          name: 'catalog-seating',
          component: () => import('@/views/seating/SeatingPlansView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            requiresSeatingPro: true,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'seating.plansTitle' },
            ],
          },
        },
        {
          path: 'catalog/seating/:planId',
          name: 'catalog-seating-plan',
          component: () => import('@/views/seating/SeatingPlanDetailView.vue'),
          meta: {
            permission: STAFF_PERMISSIONS.catalogRead,
            requiresSeatingPro: true,
            breadcrumbs: [
              { labelKey: 'nav.catalog', path: '/catalog/locations' },
              { labelKey: 'seating.plansTitle', path: '/catalog/seating' },
              { labelKey: 'seating.planDetail' },
            ],
          },
        },
        {
          path: ':pathMatch(.*)*',
          name: 'not-found',
          component: () => import('@/views/NotFoundView.vue'),
          meta: { titleKey: 'notFound.title' },
        },
      ],
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }

  if (to.meta.requiresAuth) {
    if (!auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }
  }

  const requiredFromMeta = (to.meta.permissions as string[] | undefined) ?? []
  const singlePerm = to.meta.permission as string | undefined
  const required =
    requiredFromMeta.length > 0 ? requiredFromMeta : singlePerm ? [singlePerm] : []

  if (required.length > 0 && auth.isAuthenticated) {
    const list = normalizeStaffPermissionList(auth.user?.role?.permissions)
    const ok = list.includes('*') || required.every((p) => list.includes(p))
    if (!ok) {
      return { name: 'access-denied', query: { permission: required.join(',') } }
    }
  }

  const requiredAny = (to.meta.permissionsAny as string[] | undefined) ?? []
  if (requiredAny.length > 0 && auth.isAuthenticated) {
    const list = normalizeStaffPermissionList(auth.user?.role?.permissions)
    const ok = list.includes('*') || requiredAny.some((p) => list.includes(p))
    if (!ok) {
      return { name: 'access-denied', query: { permission: requiredAny.join(',') } }
    }
  }

  const ownerOnlyVendorScope = to.meta.ownerOnlyVendorScope === true && auth.isAuthenticated
  if (ownerOnlyVendorScope && auth.user?.vendorScope?.mode === 'restricted') {
    return {
      name: 'access-denied',
      query: { reason: 'owner_scope', redirect: to.fullPath },
    }
  }

  const tenantBrandingStore = useTenantBrandingStore()
  const needsSeatingPro = to.meta.requiresSeatingPro === true && auth.isAuthenticated
  const needsGiftCards = to.meta.requiresGiftCards === true && auth.isAuthenticated
  const needsVendorSystem = to.meta.requiresVendorSystem === true && auth.isAuthenticated
  const needsAnalytics = to.meta.requiresAnalytics === true && auth.isAuthenticated
  if (needsSeatingPro || needsGiftCards || needsVendorSystem || needsAnalytics) {
    await tenantBrandingStore.load()
  }
  if (needsSeatingPro && !tenantBrandingStore.seatingProActive) {
    return {
      name: 'access-denied',
      query: { reason: 'seating_pro', redirect: to.fullPath },
    }
  }
  if (needsGiftCards && !tenantBrandingStore.giftCardsActive) {
    return {
      name: 'access-denied',
      query: { reason: 'gift_cards', redirect: to.fullPath },
    }
  }
  if (needsVendorSystem && !tenantBrandingStore.vendorSystemActive) {
    return {
      name: 'access-denied',
      query: { reason: 'vendor_system', redirect: to.fullPath },
    }
  }
  if (needsAnalytics && !tenantBrandingStore.analyticsActive) {
    return {
      name: 'access-denied',
      query: { reason: 'analytics', redirect: to.fullPath },
    }
  }

  return true
})

router.afterEach((to) => {
  syncDocumentTitle(to)
})

export default router
